import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { alertsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CreateAlertDialogProps {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface AlertFormData {
  type: "price_drop" | "threshold";
  threshold: string;
  channels: {
    telegram: boolean;
    email: boolean;
  };
}

export default function CreateAlertDialog({ tripId, open, onOpenChange }: CreateAlertDialogProps) {
  const queryClient = useQueryClient();
  const [channelError, setChannelError] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<AlertFormData>({
    defaultValues: {
      type: "price_drop",
      threshold: "",
      channels: { telegram: false, email: false },
    },
  });

  const alertType = watch("type");

  const createMutation = useMutation({
    mutationFn: (data: { type: string; threshold?: number; channels: string[] }) =>
      alertsApi.create(tripId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trip", tripId] });
      queryClient.invalidateQueries({ queryKey: ["alerts", tripId] });
      reset();
      setChannelError("");
      onOpenChange(false);
    },
  });

  const onSubmit = (data: AlertFormData) => {
    const selectedChannels: string[] = [];
    if (data.channels.telegram) selectedChannels.push("telegram");
    if (data.channels.email) selectedChannels.push("email");

    if (selectedChannels.length === 0) {
      setChannelError("At least one channel is required");
      return;
    }
    setChannelError("");

    createMutation.mutate({
      type: data.type,
      threshold: data.type === "threshold" ? Number(data.threshold) : undefined,
      channels: selectedChannels,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Alert</DialogTitle>
          <DialogDescription>
            Get notified when flight prices drop or reach your target threshold.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Alert Type</Label>
            <Select
              value={alertType}
              onValueChange={(value: "price_drop" | "threshold") => setValue("type", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select alert type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="price_drop">Price Drop</SelectItem>
                <SelectItem value="threshold">Price Threshold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {alertType === "threshold" && (
            <div className="space-y-2">
              <Label htmlFor="threshold">Price Threshold ($)</Label>
              <Input
                id="threshold"
                type="number"
                min="1"
                step="1"
                placeholder="e.g. 500"
                {...register("threshold", {
                  required: alertType === "threshold" ? "Threshold is required" : false,
                  min: { value: 1, message: "Must be at least " },
                })}
              />
              {errors.threshold && (
                <p className="text-sm text-destructive">{errors.threshold.message}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Notification Channels</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-primary"
                  {...register("channels.telegram")}
                />
                <span className="text-sm">Telegram</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-primary"
                  {...register("channels.email")}
                />
                <span className="text-sm">Email</span>
              </label>
            </div>
            {channelError && (
              <p className="text-sm text-destructive">{channelError}</p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create Alert"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
